/**
 * The closed vocabularies of *Accesos*, each mapped to a glyph and a
 * word (Plan J, J3).
 *
 * ONE RULE, AND IT IS THE REASON THIS FILE IS NOT INLINE
 * -----------------------------------------------------
 * A backend identifier never reaches the screen prettified.
 * `resolveStatus` is the only thing that enforces it: a value with no
 * entry comes back with `unknown: true`, the raw key, a warning tone and
 * a `font-mono` treatment. J2 measured what happens without it —
 * swapping `resolveStatus` for `dict[raw] ?? titleCase(raw)` put
 * `Column Not In Raw` in front of an analyst with a neutral dash glyph,
 * a designed-looking label for a state nobody designed, and left every
 * test and `tsc` green.
 *
 * So the dictionaries live here, the CELLS that use them live here too,
 * and `__tests__/scope-status.test.tsx` renders the cells rather than
 * asserting on the dictionaries.
 *
 * ON THE TONES, where the mapping is a judgement and not obvious
 * --------------------------------------------------------------
 * * `no_mapping` is **success**, not warning. It PUBLISHED a file — an
 *   empty one — and is listed beside `built` in `cli._OUTCOME_HAS_FILE`.
 *   The runbook says so in as many words: *"Si automatizas esto,
 *   `no_mapping` es verde: fíltralo por status, no por ausencia de
 *   warnings."* The reader sees an empty dashboard, not a 503.
 * * `not_applicable` is **neutral**, not danger. It is a correct
 *   configuration: this project declares no dimension the rules name, so
 *   the scope grants no access to it. The rebuild endpoint answers 200
 *   for the same reason — a 4xx would read as "you did something wrong".
 * * `never_built` is **warning**: the scope resolves for somebody and
 *   there is no file, which is a 503 with
 *   `X-Tenant-Resolution: scope-not-built` on every report call until
 *   somebody builds it. It is the state the runbook's step-5
 *   verification exists to catch before the person phones.
 * * `grants_nothing` is **danger** and not warning. Whoever joins that
 *   group opens no report at all, and in `SELECT * FROM groups` it looks
 *   perfect.
 */

import type { StatusDef } from "@pervasivemind/admin-ui";

import { SCOPE_ALL } from "@/lib/access-api";

export type TFn = (
  key: string,
  params?: Record<string, string | number>,
) => string;

/**
 * Does THIS group's cut decide what a member reads? (Plan J, J3.)
 *
 * WHY A MEMBER ROW NEEDS A VERDICT AND NOT A FACT
 * -----------------------------------------------
 * A restricted group's Miembros tab used to render one grey line —
 * "unión de N ámbitos" — beside the effective id. That is a fact, and a
 * fact is the wrong shape here: the person whose restriction is INERT
 * (they are also in an unrestricted group, so they read the whole data
 * set) rendered identically to the person whose restriction is the one
 * they actually read through. Two rows, opposite meanings, same
 * appearance, on the one cell an operator is being taught to trust when
 * they ask "did the restriction take?".
 *
 * The union rules are why the difference is invisible without help.
 * `scopes/effective.union_scopes` R1: if ANY group of the person carries
 * `all`, the union IS `all` — the restriction on this page decides
 * nothing. R3: a wider group of theirs can CONTAIN this one, and then
 * the effective id is that other scope's. Neither shows up in this
 * group's own row, its own name, or its own scope id.
 *
 * WHAT EACH VERDICT CLAIMS, AND WHAT IT REFUSES TO CLAIM
 * -----------------------------------------------------
 * * `unrestricted` — the person's binding IS `all`. Categorical: they
 *   read every row, and nothing this page says about a cut applies to
 *   them. `group_scope_ids` usually shows the culprit (another group
 *   carrying `all`), but the verdict does not depend on being able to
 *   name it: the READ is the whole data set either way.
 * * `elsewhere` — the binding names a scope that is not this group's.
 *   The claim stops there, deliberately. The union can only widen, so
 *   this is normally "wider"; a binding the recompute has not caught up
 *   with is the other way to get here, and calling that one "wider"
 *   would be asserting a direction this row cannot prove. "Decided
 *   somewhere else" is true in both.
 * * `holds` — the binding is exactly this group's scope, so what this
 *   page restricts is what the person reads.
 * * `not_applicable` — this group carries `all`. There is no cut here
 *   to be inert, so every row would earn the same verdict and the
 *   column would be noise. Chips are for a difference that exists.
 */
export type MemberScopeVerdict =
  | "not_applicable"
  | "unrestricted"
  | "elsewhere"
  | "holds";

export function memberScopeVerdict(
  groupScopeId: string,
  effectiveScopeId: string,
): MemberScopeVerdict {
  if (groupScopeId === SCOPE_ALL) return "not_applicable";
  if (effectiveScopeId === SCOPE_ALL) return "unrestricted";
  return effectiveScopeId === groupScopeId ? "holds" : "elsewhere";
}

/**
 * The three renderable verdicts, each a glyph and a word.
 *
 * `holds` is rendered too, and that is the point rather than decoration:
 * if only the bad rows carried a chip, "no chip" would have to mean
 * "fine", and an absent element is exactly the signal a reader skips.
 * The affirmative chip is what makes the warning legible as a
 * DIFFERENCE. Tones stay inside the band pair `StatusChip` documents —
 * the glyph and the label carry the meaning, never the colour alone.
 */
export function memberScopeDict(t: TFn): Record<string, StatusDef> {
  return {
    unrestricted: {
      label: t("access.groups.members.scope.unrestricted"),
      tone: "warning",
    },
    elsewhere: {
      label: t("access.groups.members.scope.elsewhere"),
      tone: "warning",
    },
    holds: { label: t("access.groups.members.scope.holds"), tone: "success" },
  };
}

/** `SCOPE_PROJECT_STATES` — what one (scope, project) pair IS. */
export function scopeStateDict(t: TFn): Record<string, StatusDef> {
  return {
    built: { label: t("access.scopes.state.built"), tone: "success" },
    no_mapping: { label: t("access.scopes.state.noMapping"), tone: "success" },
    building: { label: t("access.scopes.state.building"), tone: "running" },
    failed: { label: t("access.scopes.state.failed"), tone: "danger" },
    not_applicable: {
      label: t("access.scopes.state.notApplicable"),
      tone: "neutral",
    },
    never_built: {
      label: t("access.scopes.state.neverBuilt"),
      tone: "warning",
    },
  };
}

/**
 * `scope_builds.status` plus `skipped_concurrent`.
 *
 * `skipped_concurrent` never reaches the table — no row is claimed when
 * it happens — so it appears only in a rebuild response, and it is NOT
 * an error: `uq_scope_builds_active` is the cross-process double-run
 * guard and it arbitrated. Painting it `danger` would send an operator
 * looking for a fault that is a working safety mechanism.
 */
export function buildStatusDict(t: TFn): Record<string, StatusDef> {
  return {
    building: { label: t("access.scopes.state.building"), tone: "running" },
    built: { label: t("access.scopes.state.built"), tone: "success" },
    failed: { label: t("access.scopes.state.failed"), tone: "danger" },
    no_mapping: { label: t("access.scopes.state.noMapping"), tone: "success" },
    not_applicable: {
      label: t("access.scopes.state.notApplicable"),
      tone: "neutral",
    },
    skipped_concurrent: {
      label: t("access.scopes.state.skippedConcurrent"),
      tone: "info",
    },
  };
}

/** `GROUP_HEALTH`, worst-first. */
export function groupHealthDict(t: TFn): Record<string, StatusDef> {
  return {
    grants_nothing: {
      label: t("access.groups.health.grantsNothing"),
      tone: "danger",
    },
    no_members: { label: t("access.groups.health.noMembers"), tone: "warning" },
    ok: { label: t("access.groups.health.ok"), tone: "success" },
  };
}

/**
 * `service_access_audit._ACTION_TARGETS`, one label each.
 *
 * Pinned to the Python literal by `__tests__/audit-actions.test.ts`. An
 * action the backend can write and this table cannot name would render
 * as a raw dotted identifier — which `resolveStatus` does honestly, and
 * which the test exists to prevent anyway.
 */
export function auditActionDict(t: TFn): Record<string, StatusDef> {
  return {
    "dimension.create": {
      label: t("access.audit.action.dimensionCreate"),
      tone: "info",
    },
    "dimension.update": {
      label: t("access.audit.action.dimensionUpdate"),
      tone: "info",
    },
    "dimension.delete": {
      label: t("access.audit.action.dimensionDelete"),
      tone: "danger",
    },
    "scope.create": {
      label: t("access.audit.action.scopeCreate"),
      tone: "info",
    },
    "scope.update": {
      label: t("access.audit.action.scopeUpdate"),
      tone: "warning",
    },
    "scope.delete": {
      label: t("access.audit.action.scopeDelete"),
      tone: "danger",
    },
    "scope.rebuild": {
      label: t("access.audit.action.scopeRebuild"),
      tone: "neutral",
    },
    "group.create": {
      label: t("access.audit.action.groupCreate"),
      tone: "info",
    },
    "group.update": {
      label: t("access.audit.action.groupUpdate"),
      tone: "warning",
    },
    "group.delete": {
      label: t("access.audit.action.groupDelete"),
      tone: "danger",
    },
    "group.permission_set": {
      label: t("access.audit.action.groupPermissionSet"),
      tone: "warning",
    },
    "group.member_add": {
      label: t("access.audit.action.groupMemberAdd"),
      tone: "info",
    },
    "group.member_remove": {
      label: t("access.audit.action.groupMemberRemove"),
      tone: "danger",
    },
    "user.effective_scope_change": {
      label: t("access.audit.action.userEffectiveScopeChange"),
      tone: "warning",
    },
    // The levers J3 inherited rather than built. `danger` is for the
    // three that take access away or hand it out wholesale — a role
    // change can make somebody an admin, and an admin bypasses every
    // cell in both resolvers.
    "role_default.update": {
      label: t("access.audit.action.roleDefaultUpdate"),
      tone: "warning",
    },
    "user.create": {
      label: t("access.audit.action.userCreate"),
      tone: "info",
    },
    "user.role_change": {
      label: t("access.audit.action.userRoleChange"),
      tone: "danger",
    },
    "user.deactivate": {
      label: t("access.audit.action.userDeactivate"),
      tone: "danger",
    },
    "user.reactivate": {
      label: t("access.audit.action.userReactivate"),
      tone: "warning",
    },
    // Both directions are ONE action — the row carries the boolean in
    // its before and after — so the tone cannot follow the direction.
    // `warning` and not `danger`: it removes the seat and keeps every
    // delivery, which is narrower than a deactivation.
    "user.login_allowed_change": {
      label: t("access.audit.action.userLoginAllowedChange"),
      tone: "warning",
    },
    "user.delete": {
      label: t("access.audit.action.userDelete"),
      tone: "danger",
    },
    "user.password_reset": {
      label: t("access.audit.action.userPasswordReset"),
      tone: "warning",
    },
    "user.builder_access": {
      label: t("access.audit.action.userBuilderAccess"),
      tone: "warning",
    },
    "view_as.scope_preview": {
      label: t("access.audit.action.viewAsScopePreview"),
      tone: "neutral",
    },
  };
}

/** `ck_access_audit_actor_kind` — the SURFACE a change came through. */
export function actorKindDict(t: TFn): Record<string, StatusDef> {
  return {
    provider_admin: {
      label: t("access.audit.actor.providerAdmin"),
      tone: "info",
    },
    tenant_admin: { label: t("access.audit.actor.tenantAdmin"), tone: "neutral" },
    platform: { label: t("access.audit.actor.platform"), tone: "warning" },
    system: { label: t("access.audit.actor.system"), tone: "neutral" },
  };
}
